import { useRef, useState, type KeyboardEvent } from 'react';
import type { WireMessage } from '@converoom/contracts';
import { api, ApiError } from '../lib/api';
import { EmojiButton, insertAt } from './EmojiPicker';

interface Props {
  roomId: string;
  aiEnabled: boolean;
  replyTo: WireMessage | null;
  onCancelReply: () => void;
  onSend: (text: string, attachmentIds: string[]) => Promise<boolean>;
  onTyping: () => void;
  mentionStatus: (text: string) => { provider: string; label: string; connected: boolean }[];
  /** AIs kept on for follow-ups ("devam"); empty = off. */
  followUp: { provider: string; label: string }[];
  onClearFollowUp: () => void;
}

export function Composer({
  roomId,
  aiEnabled,
  replyTo,
  onCancelReply,
  onSend,
  onTyping,
  mentionStatus,
  followUp,
  onClearFollowUp,
}: Props) {
  const [text, setText] = useState('');
  const [files, setFiles] = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const lastTyping = useRef(0);
  const fileInput = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const mentions = mentionStatus(text);

  async function submit() {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    const ok = await onSend(
      t,
      files.map((f) => f.id),
    );
    setBusy(false);
    if (ok) {
      setText('');
      setFiles([]);
    }
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (
      e.key === 'Enter' &&
      !e.shiftKey &&
      !e.nativeEvent.isComposing &&
      window.matchMedia('(pointer: fine)').matches
    ) {
      e.preventDefault();
      void submit();
    }
  }

  async function upload(f: File) {
    setUploadError(null);
    try {
      const r = await api.upload<{ id: string; name: string }>(`/v1/rooms/${roomId}/files`, f);
      setFiles((x) => [...x, { id: r.id, name: r.name }]);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : 'network';
      setUploadError(
        code === 'mime_not_allowed'
          ? 'Bu dosya türü kabul edilmiyor (metin, PDF, PNG/JPEG/WebP).'
          : `Dosya yüklenemedi (${code}).`,
      );
    }
  }

  return (
    <div className="composer">
      <div className="composer-inner">
        {replyTo && (
          <div className="quote" style={{ marginBottom: 'var(--s2)' }}>
            Yanıtlanan: {replyTo.authorName} — {replyTo.text.slice(0, 120)}{' '}
            <button className="btn small ghost" onClick={onCancelReply}>
              Vazgeç
            </button>
          </div>
        )}
        <div className="composer-box">
          <label htmlFor="composer-text" className="sr-only">
            Odaya mesaj
          </label>
          <textarea
            id="composer-text"
            ref={textRef}
            rows={2}
            value={text}
            placeholder={
              aiEnabled
                ? 'Mesaj yazın. AI için @claude, @codex, @gemini veya ücretsiz @ai ekleyin.'
                : 'Mesaj yazın. Bu odada AI kapalı.'
            }
            onChange={(e) => {
              setText(e.target.value);
              if (Date.now() - lastTyping.current > 2500) {
                lastTyping.current = Date.now();
                onTyping();
              }
            }}
            onKeyDown={onKey}
            enterKeyHint="send"
          />
          <div className="composer-row">
            {files.map((f) => (
              <span className="chip" key={f.id}>
                {f.name}
                <button
                  aria-label={`${f.name} ekini kaldır`}
                  onClick={() => setFiles((x) => x.filter((y) => y.id !== f.id))}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              ref={fileInput}
              type="file"
              hidden
              accept=".txt,.md,.csv,.json,.pdf,image/png,image/jpeg,image/webp"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void upload(f);
                e.target.value = '';
              }}
            />
            <EmojiButton onPick={(em) => setText((v) => insertAt(textRef.current, v, em))} />
            <button className="btn small ghost" type="button" onClick={() => fileInput.current?.click()}>
              Dosya ekle
            </button>
            {aiEnabled && followUp.length > 0 && (
              <span className="chip" title="Etiket yazmadığınız mesajlar da bu AI'a gider">
                Devam: {followUp.map((f) => f.label).join(', ')}
                <button aria-label="AI ile devamı kapat" onClick={onClearFollowUp}>
                  ×
                </button>
              </span>
            )}
            <span className="invoke-hint" aria-live="polite">
              {mentions.length === 0
                ? 'AI çağrılmayacak'
                : mentions.map((m, i) => (
                    <span key={m.provider}>
                      {i > 0 && ', '}
                      <strong>{m.label}</strong>{' '}
                      {!aiEnabled
                        ? '(odada AI kapalı)'
                        : m.connected
                          ? 'sizin bağlantınızla çağrılacak'
                          : '— bağlantınız yok, çağrılmayacak'}
                    </span>
                  ))}
            </span>
            <span className="spacer" />
            <button
              className="btn primary"
              onClick={submit}
              disabled={busy || !text.trim()}
              aria-label="Odaya gönder"
            >
              {busy ? 'Gönderiliyor…' : 'Gönder'}
            </button>
          </div>
          {uploadError && (
            <p className="error-text" role="alert">
              {uploadError}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
